/**
 * Snowplow visitor identity for email capture.
 *
 * The host app injects the handler instead of this package importing a tracker,
 * for two reasons:
 *
 * - The widget build is embedded on third-party pages that have no tracker, so
 *   it must not carry one.
 * - A visitor may only be identified by email while anonymous, and only the
 *   host app knows the auth state.
 *
 * The app's tracker component drives three modes:
 *
 * - `suppressed` (the default) — drop submissions. The widget build, an app
 *   with tracking disabled, and an authenticated visitor all sit here.
 * - `pending` — the auth check has not answered yet. Hold the submitted email
 *   so it is not lost, and replay it if the visitor resolves as anonymous.
 * - `active` — the visitor is anonymous. Identify immediately.
 *
 * Suppressing discards anything held, so an email submitted while the visitor
 * was authenticated can never be replayed after they log out.
 */

'use client';

type IdentifyVisitor = (email: string) => void | Promise<void>;

let identifyVisitor: IdentifyVisitor | null = null;
let mode: 'suppressed' | 'pending' | 'active' = 'suppressed';
let heldEmail: string | null = null;

/**
 * Hold submitted emails until the auth check answers. Call this while the auth
 * state is still unknown, then call `configureVisitorIdentity` once it resolves.
 */
export const deferVisitorIdentity = (): void => {
  identifyVisitor = null;
  mode = 'pending';
};

/**
 * Register the handler that identifies an anonymous visitor by email, replaying
 * anything held while the auth check was in flight. Pass `null` to stop
 * identifying — the visitor is authenticated, or tracking is off — which also
 * discards anything held.
 */
export const configureVisitorIdentity = (
  handler: IdentifyVisitor | null
): void => {
  identifyVisitor = handler;

  if (!handler) {
    mode = 'suppressed';
    heldEmail = null;
    return;
  }

  mode = 'active';

  if (heldEmail) {
    const email = heldEmail;
    heldEmail = null;
    dispatch(handler, email);
  }
};

/**
 * Hand a raw email the visitor just gave us to the registered handler.
 * Fire-and-forget: identifying must never block or fail the submit.
 */
export const identifyVisitorByEmail = (email: string): void => {
  if (!email) {
    return;
  }

  if (mode === 'pending') {
    heldEmail = email;
    return;
  }

  if (mode !== 'active' || !identifyVisitor) {
    return;
  }

  dispatch(identifyVisitor, email);
};

function dispatch(handler: IdentifyVisitor, email: string) {
  try {
    const result = handler(email);

    if (result instanceof Promise) {
      result.catch(reportIdentityFailure);
    }
  } catch (error) {
    reportIdentityFailure(error);
  }
}

function reportIdentityFailure(error: unknown) {
  if (typeof console !== 'undefined' && console.warn) {
    // biome-ignore lint/suspicious/noConsole: a silent identity failure is unobservable otherwise
    console.warn('[bx-forms] Failed to identify visitor by email:', error);
  }
}
